import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { siteConfig, locations } from '@/data/siteData';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  MessageSquare
} from 'lucide-react';

export const ContactPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Us - Felix Infotech | Get in Touch for IT Solutions</title>
        <meta name="description" content="Contact Felix Infotech for enterprise IT, Cloud, and Cyber Security solutions. Offices in Chennai, Bangalore, Mumbai, Hyderabad, Cochin, Coimbatore, Dubai & Singapore. Call +91-44-48645444." />
        <meta name="keywords" content="contact Felix Infotech, IT company Chennai contact, enterprise IT support, Felix Infotech phone number, Felix Infotech address, IT solutions enquiry" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Felix Infotech | Get in Touch" />
        <meta property="og:description" content="Reach out for enterprise IT solutions. Offices across India, Dubai & Singapore." />
        <meta property="og:url" content="https://felixinfotech.com/contact" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Felix Infotech" />
        <meta name="twitter:description" content="Get in touch for enterprise IT, Cloud, and Cyber Security solutions." />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Felix Infotech Pvt Ltd",
            "url": "https://felixinfotech.com",
            "telephone": "+91-44-48645444",
            "email": "info@felixinfotech.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Old No.11, New No.28, Mc Nichols Road, 3rd Lane, Chetpet",
              "addressLocality": "Chennai",
              "addressRegion": "Tamil Nadu",
              "postalCode": "600031",
              "addressCountry": "IN"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "09:00",
              "closes": "18:00"
            },
            "priceRange": "$$"
          }
        `}</script>
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Animated Hero Section */}
        <AnimatedPageHero
          title="Get In Touch"
          subtitle="We're Here to Help"
          description="Ready to transform your business? Let's discuss how we can help you achieve your technology goals."
          badge="Contact Us"
          animationType="contact"
          gradient="from-violet-500 to-purple-600"
          breadcrumbs={[{ label: 'Contact Us' }]}
        />

        {/* Contact Info Cards */}
        <section className="py-20" ref={ref}>
          <div className="container mx-auto px-4 lg:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                { icon: Phone, title: 'Phone', value: siteConfig.phone, href: `tel:${siteConfig.phone}` },
                { icon: Mail, title: 'Email', value: siteConfig.email, href: `mailto:${siteConfig.email}` },
                { icon: Clock, title: 'Working Hours', value: 'Mon - Sat: 9AM - 6PM', href: null },
                { icon: MessageSquare, title: 'Live Chat', value: 'Chat with us', href: '#' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-6 shadow-soft border border-border text-center hover:shadow-elevated transition-shadow"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4 shadow-glow">
                    <item.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                  {item.href ? (
                    <a href={item.href} className="text-primary hover:underline">{item.value}</a>
                  ) : (
                    <p className="text-muted-foreground">{item.value}</p>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Office Locations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="mb-16"
            >
              <h2 className="font-display text-3xl font-bold text-foreground text-center mb-12 section-title">
                Our Office Locations
              </h2>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {locations.map((location, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="bg-card rounded-2xl p-6 shadow-soft border border-border hover:shadow-elevated transition-all hover:-translate-y-1"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-lg mb-2">{location.city}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{location.address}</p>
                        <a href={`tel:${location.phone}`} className="text-sm text-primary hover:underline flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {location.phone}
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="rounded-2xl overflow-hidden shadow-elevated border border-border h-[400px]"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0123456789!2d80.2059078!3d13.0258449!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267033b116bf5%3A0x2a3259c80aaf434b!2sFelix%20Infotech%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Felix Infotech Location"
              />
            </motion.div>
          </div>
        </section>

        {/* Contact Form Section */}
        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default ContactPage;
